import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { vehicleService } from '../../api/services/vehicleService';
import { VehicleExportDto } from '../../../../shared/types';
import { buildPartsListHtml, PartType } from './printPartsListHtml';
import { Loader2 } from 'lucide-react';

// Renders a bookmarkable/shareable print view, e.g.
// /vehicle-applications/vehicles/print?id=58&id=73&id=31&printPartList=true&partType=Seat+Covers
const PrintPartsList: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const printedRef = useRef(false);

  const makeIds = searchParams.getAll('id').map(id => parseInt(id, 10)).filter(id => !isNaN(id));
  const partType = (searchParams.get('partType') as PartType) || 'Seat Covers';
  const shouldPrint = searchParams.get('printPartList') === 'true';

  useEffect(() => {
    if (makeIds.length === 0) {
      setError('No makes specified.');
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const data: VehicleExportDto[] = partType === 'Cargo Liner'
          ? await vehicleService.getPrintCargoPartsListData(makeIds)
          : partType === 'Dash Cover'
          ? await vehicleService.getPrintDashPartsListData(makeIds)
          : await vehicleService.getPrintPartsListData(makeIds);

        const html = buildPartsListHtml(data, partType);
        const bodyMatch = html.match(/<body>([\s\S]*)<\/body>/);
        if (containerRef.current) {
          containerRef.current.innerHTML = bodyMatch ? bodyMatch[1] : html;
        }
      } catch {
        setError('Failed to load print data.');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loading && !error && shouldPrint && !printedRef.current) {
      printedRef.current = true;
      setTimeout(() => window.print(), 200);
    }
  }, [loading, error, shouldPrint]);

  // index.html hardcodes a dark <html> background for the app shell; force this
  // standalone print page to light mode regardless of the user's app theme.
  useEffect(() => {
    const html = document.documentElement;
    const prevBg = html.style.backgroundColor;
    const hadDarkClass = html.classList.contains('dark');
    html.style.backgroundColor = '#ffffff';
    html.classList.remove('dark');
    return () => {
      html.style.backgroundColor = prevBg;
      if (hadDarkClass) html.classList.add('dark');
    };
  }, []);

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 10, margin: 20, minHeight: '100vh', backgroundColor: '#ffffff', color: '#111111' }}>
      <style>{`
        h1 { font-size: 15px; margin-bottom: 4px; }
        h2 { font-size: 12px; margin: 16px 0 4px; background: #ddd; padding: 3px 6px; color: #111; }
        table { border-collapse: collapse; width: 100%; margin-bottom: 12px; }
        th, td { border: 1px solid #aaa; padding: 2px 5px; text-align: left; white-space: nowrap; color: #111; background: #fff; }
        th { background: #eee; font-weight: bold; }
        .print-btn { margin-bottom: 12px; padding: 6px 14px; cursor: pointer; font-size: 13px; }
        @media print { .print-btn { display: none; } }
      `}</style>
      {loading && (
        <div className="flex items-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading parts list…
        </div>
      )}
      {error && <div style={{ color: 'red' }}>{error}</div>}
      <div ref={containerRef} />
    </div>
  );
};

export default PrintPartsList;
